import React, { useState, useEffect, useCallback } from "react";
import {
  CFormSelect,
  CFormInput,
  CFormCheck,
  CButton,
  CSpinner,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import axios from "axios";
import AddCombined from "./AddCombined";

const DefectPropertiesPanel = ({
  objectData,
  token,
  selectedPhoto,
  selectedFirstLevelClass: propSelectedFirstLevelClass,
  selectedSecondLevelClass: propSelectedSecondLevelClass,
  onFirstLevelChange,
  onSecondLevelChange,
  selectedUserBox,
  selectedBoxInfo,
  annotations = [],
  additionalAnnotations = [],
  apiDataForPropertiesPanel,
  onSaveSuccess,
  onRequestOpenSettings,
}) => {
  const [allFirstLevelClasses, setAllFirstLevelClasses] = useState([]);
  const [allSecondLevelClasses, setAllSecondLevelClasses] = useState([]);
  const modalStyle = {
    height: "auto",
    maxWidth: "500px",
    display: "flex",
    flexDirection: "column",
  };

  // ЛОКАЛЬНОЕ СОСТОЯНИЕ ДЛЯ КОНСТРУКЦИИ И ДЕФЕКТА
  const [localFirstLevelClass, setLocalFirstLevelClass] = useState(
    propSelectedFirstLevelClass,
  );
  const [localSecondLevelClass, setLocalSecondLevelClass] = useState(
    propSelectedSecondLevelClass,
  );

  const [defectProperties, setDefectProperties] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingToAll, setApplyingToAll] = useState(false);
  const [edited, setEdited] = useState(false);
  const [dcData, setDcData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editModalData, setEditModalData] = useState(null);
  const [formData, setFormData] = useState({
    DefectClassID: "",
    ClassName: "",
    Location: "",
    QuantitativeValue: "",
    IsUnionToPhoto: false,
    Category: "",
    DefectCharacter: "",
    Recommendation: "",
    Cause: "",
    boxX: null,
    boxY: null,
    boxW: null,
    boxH: null,
  });

  const [loadingSecondLevelClasses, setLoadingSecondLevelClasses] =
    useState(false);
  const [selectedDefectDisplay, setSelectedDefectDisplay] = useState("");

  // СОСТОЯНИЯ ДЛЯ КАСТОМНОГО МОДАЛЬНОГО ОКНА ПОДТВЕРЖДЕНИЯ
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingConstructionChange, setPendingConstructionChange] =
    useState(null);

  // Синхронизация при смене пропса (клики по дереву слева)
  useEffect(() => {
    setLocalFirstLevelClass(propSelectedFirstLevelClass);
    setLocalSecondLevelClass(propSelectedSecondLevelClass);
  }, [propSelectedFirstLevelClass, propSelectedSecondLevelClass]);

  const parseDcData = (dcString) => {
    if (!dcString) return null;
    try {
      const parsed = JSON.parse(dcString);
      return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
      console.error("Ошибка парсинга DC данных:", error);
      return null;
    }
  };

  const extractClassID = (fullString) => {
    if (!fullString) return "";
    const str = fullString.toString();
    const parts = str.split(" - ");
    return parts.length > 0 ? parts[0].trim() : str.trim();
  };

  const formatOption = (item) =>
    `${item.ClassID} - ${item.ClassName || item.Item || ""}`;

  // Загрузка конструкций
  useEffect(() => {
    const fetchFirstLevelClasses = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        let url = `${baseUrl}/api/neural_network_module/firstlevelclasses/`;

        const params = {};
        if (objectData?.NTypeObj) {
          params.building_type_id = objectData.NTypeObj;
          url = `${url}?building_type_id=${objectData.NTypeObj}`;
        }

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          ...(Object.keys(params).length > 0 && { params }),
        });
        setAllFirstLevelClasses(response.data || []);
      } catch (error) {
        console.error("Ошибка загрузки firstlevelclasses:", error);
      }
    };

    fetchFirstLevelClasses();
  }, [token, objectData]);

  // Загрузка дефектов
  const fetchFilteredSecondLevelClasses = useCallback(
    async (buildingTypeId, firstLevelClassText) => {
      if (!buildingTypeId || !firstLevelClassText) {
        setAllSecondLevelClasses([]);
        return;
      }

      setLoadingSecondLevelClasses(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        const response = await axios.get(
          `${baseUrl}/api/neural_network_module/secondlevelclasses/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              building_type_id: buildingTypeId,
              first_level_class: firstLevelClassText,
            },
          },
        );

        const filteredData = response.data || [];
        setAllSecondLevelClasses(filteredData);

        if (localSecondLevelClass && filteredData.length > 0) {
          const isSelectedInList = filteredData.some(
            (item) => item.ClassID === localSecondLevelClass,
          );
          if (!isSelectedInList && onSecondLevelChange) {
            setLocalSecondLevelClass("");
            onSecondLevelChange("");
            setSelectedDefectDisplay("");
          }
        }
      } catch (error) {
        console.error(
          "Ошибка загрузки secondlevelclasses с фильтрацией:",
          error,
        );
        setAllSecondLevelClasses([]);
      } finally {
        setLoadingSecondLevelClasses(false);
      }
    },
    [token, localSecondLevelClass, onSecondLevelChange],
  );

  // Авто-загрузка дефектов при смене локального класса
  useEffect(() => {
    if (localFirstLevelClass && objectData?.NTypeObj) {
      const selectedFirstLevelClassObj = allFirstLevelClasses.find(
        (item) => item.ClassID === localFirstLevelClass,
      );

      if (selectedFirstLevelClassObj) {
        const firstLevelClassText = `${selectedFirstLevelClassObj.ClassID} - ${selectedFirstLevelClassObj.ClassName}`;
        fetchFilteredSecondLevelClasses(
          objectData.NTypeObj,
          firstLevelClassText,
        );
      }
    }
  }, [
    localFirstLevelClass,
    objectData?.NTypeObj,
    allFirstLevelClasses,
    fetchFilteredSecondLevelClasses,
  ]);

  // Получение характеристик из справочника
  const fetchDefectCharacteristics = useCallback(
    async (overrideFirstId = null, overrideSecondId = null) => {
      const firstClassId = overrideFirstId || localFirstLevelClass;
      const secondClassId = overrideSecondId || localSecondLevelClass;

      if (
        !objectData?.Id ||
        !objectData?.NTypeObj ||
        !firstClassId ||
        !secondClassId
      ) {
        return;
      }

      setLoading(true);
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        const cleanFirstClassId = extractClassID(firstClassId);
        const cleanSecondClassId = extractClassID(secondClassId);

        const response = await axios.get(
          `${baseUrl}/api/objects/${objectData.Id}/defect-characteristics/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              object_type_id: objectData.NTypeObj,
              first_class_id: cleanFirstClassId,
              second_class_id: cleanSecondClassId,
            },
          },
        );

        const characteristicsData = response.data?.data;
        const data =
          Array.isArray(characteristicsData) && characteristicsData.length > 0
            ? characteristicsData[0]
            : null;

        if (data) {
          setDefectProperties(data);
          const parsedDc = parseDcData(data.DC);
          setDcData(parsedDc);

          setFormData((prev) => ({
            ...prev,
            DefectClassID: extractClassID(data.Defect) || secondClassId,
            ClassName: data.ClassName || "",
            Location: "",
            QuantitativeValue: prev.QuantitativeValue || "",
            IsUnionToPhoto: false,
            Category: "",
            DefectCharacter: "",
            Recommendation: "",
            Cause: "",
          }));

          setEdited(true);
        } else {
          setDcData(null);
          setDefectProperties(null);
        }
      } catch (error) {
        console.error("Ошибка при получении характеристик дефекта:", error);
        setDcData(null);
      } finally {
        setLoading(false);
      }
    },
    [objectData, localFirstLevelClass, localSecondLevelClass, token],
  );

  // Загрузка характеристик при открытии
  useEffect(() => {
    if (
      !apiDataForPropertiesPanel &&
      localFirstLevelClass &&
      localSecondLevelClass
    ) {
      fetchDefectCharacteristics();
    }
  }, [apiDataForPropertiesPanel]);

  // Загрузка свойств существующей рамки при клике на неё
  useEffect(() => {
    if (
      apiDataForPropertiesPanel &&
      (apiDataForPropertiesPanel.slaId ||
        apiDataForPropertiesPanel.slaID ||
        apiDataForPropertiesPanel.ID) &&
      objectData?.Id
    ) {
      setFormData((prev) => ({
        ...prev,
        ClassName: "Загрузка...",
        DefectCharacter: "",
        Recommendation: "",
        Cause: "",
        Location: "",
      }));
      setDcData(null);

      const slaId =
        apiDataForPropertiesPanel.slaId ||
        apiDataForPropertiesPanel.slaID ||
        apiDataForPropertiesPanel.ID;

      const fetchPropertiesBySlaId = async () => {
        setLoading(true);
        try {
          const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
          const response = await axios.get(
            `${baseUrl}/api/neural_network_module/${objectData.Id}/defect-properties/`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { sla_id: slaId },
            },
          );

          const data =
            Array.isArray(response.data) && response.data.length > 0
              ? response.data[0]
              : null;
          setDefectProperties(data);
          const defectClassID = data ? extractClassID(data.Defect) : "";

          // Обновляем локальные классы, не трогая родительские, чтобы не сбить фото
          if (data && data.Construct) {
            const constructID = extractClassID(data.Construct);
            setLocalFirstLevelClass(constructID);
          } else if (apiDataForPropertiesPanel?.first_class_id_str) {
            setLocalFirstLevelClass(
              apiDataForPropertiesPanel.first_class_id_str,
            );
          }

          if (defectClassID) {
            setLocalSecondLevelClass(defectClassID);
          } else if (apiDataForPropertiesPanel?.second_class_id_str) {
            setLocalSecondLevelClass(
              apiDataForPropertiesPanel.second_class_id_str,
            );
          }

          const fallbackLabel =
            apiDataForPropertiesPanel.Label ||
            apiDataForPropertiesPanel.ClassName ||
            apiDataForPropertiesPanel.Cname ||
            "";

          setFormData({
            DefectClassID: defectClassID,
            ClassName: data?.ClassName || fallbackLabel,
            Location: data?.Location || "",
            QuantitativeValue: data?.QuantitativeValue || "",
            IsUnionToPhoto: Boolean(data?.IsUnionToPhoto),
            Category: data?.Category || "",
            DefectCharacter: data?.DefectCharacter || "",
            Recommendation: data?.Recommendation || "",
            Cause: data?.Cause || "",
            boxX: selectedUserBox?.x ?? null,
            boxY: selectedUserBox?.y ?? null,
            boxW: selectedUserBox?.w ?? null,
            boxH: selectedUserBox?.h ?? null,
          });

          setEdited(false);
          const parsedDc = parseDcData(data?.DC);
          setDcData(parsedDc);
        } catch (error) {
          console.error(
            "Ошибка при загрузки свойств дефекта по sla_id:",
            error,
          );
          setDcData(null);
          setFormData((prev) => ({
            ...prev,
            ClassName:
              apiDataForPropertiesPanel.Label ||
              apiDataForPropertiesPanel.ClassName ||
              "",
          }));
        } finally {
          setLoading(false);
        }
      };
      fetchPropertiesBySlaId();
    }
  }, [apiDataForPropertiesPanel, objectData?.Id, token, selectedUserBox]);

  const extractDcFields = (dcData) => {
    if (!dcData || dcData.length === 0)
      return { defectCharacters: [], recommendations: [], causes: [] };
    const firstItem = dcData[0];
    const defectCharacters = [];
    const recommendations = [];
    const causes = [];
    if (firstItem.Details) {
      firstItem.Details.forEach((detail) => {
        if (detail.Name === "Рекомендации") {
          recommendations.push(...(detail.Values || []));
        } else if (detail.Name === "Причины") {
          causes.push(...(detail.Values || []));
        }
      });
    }
    if (firstItem.Name) {
      defectCharacters.push(firstItem.Name);
    }
    return { defectCharacters, recommendations, causes };
  };

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      boxX: selectedUserBox?.x ?? null,
      boxY: selectedUserBox?.y ?? null,
      boxW: selectedUserBox?.w ?? null,
      boxH: selectedUserBox?.h ?? null,
    }));
    setEdited(true);
  }, [selectedUserBox]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setEdited(true);
  };

  const handleCheckboxChange = (field, itemValue, checked) => {
    const currentValues = formData[field]
      ? formData[field]
          .split(";")
          .map((v) => v.trim())
          .filter(Boolean)
      : [];
    let newValues;
    if (checked) {
      newValues = [...currentValues, itemValue];
    } else {
      newValues = currentValues.filter((v) => v !== itemValue);
    }
    const newValue = newValues.length > 0 ? newValues.join("; ") : "";
    handleChange(field, newValue);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!edited || saving) return;

    setSaving(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";

      if (!localFirstLevelClass) {
        alert("Ошибка: Не выбран класс первого уровня (конструкция).");
        setSaving(false);
        return;
      }

      if (!localSecondLevelClass) {
        alert("Ошибка: Не выбран тип дефекта.");
        setSaving(false);
        return;
      }

      let firstLevelAIID = null;
      const allAnnotations = [...annotations, ...additionalAnnotations];

      const matchingAnnotation = allAnnotations.find(
        (ann) =>
          (ann.first_class_id_str === localFirstLevelClass ||
            ann.first_class_id === localFirstLevelClass) &&
          ann.flaId,
      );

      if (matchingAnnotation) {
        firstLevelAIID = matchingAnnotation.flaId;
      } else {
        if (!selectedPhoto) {
          alert(
            "Не удалось определить текущее фото. Выберите фото из списка справа.",
          );
          setSaving(false);
          return;
        }

        const taskRes = await axios.get(
          `${baseUrl}/api/neural_network_module/task-ai/?ext_object_id=${objectData.Id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const taskId = taskRes.data?.task_id;

        if (!taskId) {
          alert("TaskID не найден. Сначала запустите обработку объекта.");
          setSaving(false);
          return;
        }

        let filePathToSave = selectedPhoto.fullPath || "";
        filePathToSave = filePathToSave
          .replace(/[\\/]Thumbnails[\\/]/i, "\\")
          .replace(/\//g, "\\");

        const firstLevelData = {
          FileName: selectedPhoto.name,
          FilePath: filePathToSave,
          Name: localFirstLevelClass,
          IsMatch: 1,
          Path: " ",
          ImageSize: "1920x1080",
          Color: "#FF0000",
          CoordLat: 0,
          CoordLng: 0,
          Score: 100,
          TaskID: taskId,
        };

        const firstLevelRes = await axios.post(
          `${baseUrl}/api/neural_network_module/first-level-ai/`,
          firstLevelData,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!firstLevelRes.data || !firstLevelRes.data.id) {
          throw new Error("Не удалось создать метку конструкции в базе.");
        }
        firstLevelAIID = firstLevelRes.data.id;
      }

      let isNewBox = selectedBoxInfo?.isSavedBox === true;
      let isOverExistingApiBox = false;
      let existingApiBoxId = null;

      if (isNewBox && selectedUserBox) {
        const isBoxOverlapping = (box1, box2) => {
          const box1X = Math.min(box1.x, box1.x + box1.w);
          const box1Y = Math.min(box1.y, box1.y + box1.h);
          const box1W = Math.abs(box1.w);
          const box1H = Math.abs(box1.h);
          const box2X = Math.min(box2.x, box2.x + box2.w);
          const box2Y = Math.min(box2.y, box2.y + box2.h);
          const box2W = Math.abs(box2.w);
          const box2H = Math.abs(box2.h);
          return (
            box1X < box2X + box2W &&
            box1X + box1W > box2X &&
            box1Y < box2Y + box2H &&
            box1Y + box1H > box2Y
          );
        };

        const overlappingAnnotation = annotations.find((ann) =>
          isBoxOverlapping(selectedUserBox, ann),
        );
        if (overlappingAnnotation) {
          isOverExistingApiBox = true;
          existingApiBoxId =
            overlappingAnnotation.slaId || overlappingAnnotation.ID;
        } else {
          const overlappingAdditionalAnnotation = additionalAnnotations.find(
            (ann) => isBoxOverlapping(selectedUserBox, ann),
          );
          if (overlappingAdditionalAnnotation) {
            isOverExistingApiBox = true;
            existingApiBoxId =
              overlappingAdditionalAnnotation.slaId ||
              overlappingAdditionalAnnotation.ID;
          }
        }
      }

      const x = formData.boxX ?? selectedUserBox?.x;
      const y = formData.boxY ?? selectedUserBox?.y;
      const w = formData.boxW ?? selectedUserBox?.w;
      const h = formData.boxH ?? selectedUserBox?.h;

      if (x == null || y == null || w == null || h == null) {
        alert("Ошибка: Отсутствуют координаты рамки.");
        setSaving(false);
        return;
      }

      if (isNewBox && !isOverExistingApiBox) {
        const saveData = {
          Name: localSecondLevelClass,
          Location: formData.Location || " ",
          QuantitativeValue:
            formData.QuantitativeValue !== ""
              ? Number(formData.QuantitativeValue)
              : null,
          Category: formData.Category || null,
          Recommendation: formData.Recommendation || "",
          Cause: formData.Cause || "",
          DefectCharacter: formData.DefectCharacter || "",
          IsUnionToPhoto: formData.IsUnionToPhoto ? 1 : 0,
          FirstLevelAIID: firstLevelAIID,
          Color: "#FF0000",
          IsMatch: 1,
          Label: formData.ClassName || " ",
          x1: x,
          y1: y,
          x2: x + w,
          y2: y + h,
        };
        console.log("SAVE DATA:", saveData);
        await axios.post(
          `${baseUrl}/api/neural_network_module/second-level-ai/`,
          saveData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        alert("Новая запись успешно создана");
      } else {
        let existingRecordId = null;

        if (isOverExistingApiBox && existingApiBoxId) {
          existingRecordId = existingApiBoxId;
        } else {
          existingRecordId =
            apiDataForPropertiesPanel?.slaId ||
            apiDataForPropertiesPanel?.slaID;
          if (!existingRecordId) {
            existingRecordId = defectProperties?.SecondLevelAIID;
          }
        }

        if (!existingRecordId) {
          alert(
            "Выбранная рамка не содержит данных для обновления (отсутствует внутренний ID рамки).",
          );
          setSaving(false);
          return;
        }

        const saveData = {
          Id: existingRecordId,
          FirstLevelAIID: firstLevelAIID,
          Name: localSecondLevelClass || null,
          Location: formData.Location || "",
          QuantitativeValue:
            formData.QuantitativeValue !== ""
              ? Number(formData.QuantitativeValue)
              : null,
          Category: formData.Category || null,
          Recommendation: formData.Recommendation || "",
          Cause: formData.Cause || "",
          DefectCharacter: formData.DefectCharacter || "",
          IsUnionToPhoto: formData.IsUnionToPhoto ? 1 : 0,
          Label: formData.ClassName || "",
          x1: x,
          y1: y,
          x2: x + w,
          y2: y + h,
        };

        await axios.put(
          `${baseUrl}/api/neural_network_module/second-level-ai/`,
          saveData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        alert("Изменения успешно сохранены");
      }

      setEdited(false);
      if (onSaveSuccess)
        onSaveSuccess(
          localFirstLevelClass,
          localSecondLevelClass,
          isNewBox,
          selectedBoxInfo?.id,
        );
    } catch (error) {
      console.error("Ошибка сохранения:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message;
      alert(`Ошибка при сохранении данных: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToAll = async () => {
    if (applyingToAll) return;

    if (!objectData?.Id || !localFirstLevelClass || !localSecondLevelClass) {
      alert(
        "Недостаточно данных для массового применения (ID объекта или классы не выбраны).",
      );
      return;
    }

    const userConfirmed = window.confirm(
      `Применить свойства ко ВСЕМ дефектам типа "${localSecondLevelClass}" в конструкции "${localFirstLevelClass}" для этого объекта?`,
    );
    if (!userConfirmed) return;

    setApplyingToAll(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const saveData = {
        scope: "group",
        ExtObjectID: objectData.Id,
        FirstClassID: localFirstLevelClass,
        SecondClassID: localSecondLevelClass,
        Location: formData.Location || "",
        QuantitativeValue:
          formData.QuantitativeValue !== ""
            ? Number(formData.QuantitativeValue)
            : null,
        Category: formData.Category || null,
        Recommendation: formData.Recommendation || "",
        Cause: formData.Cause || "",
        DefectCharacter: formData.DefectCharacter || "",
        IsUnionToPhoto: formData.IsUnionToPhoto ? 1 : 0,
        Label: formData.ClassName || "",
      };

      await axios.put(
        `${baseUrl}/api/neural_network_module/second-level-ai/`,
        saveData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      alert("Изменения применены ко всем записям.");
      if (onSaveSuccess) onSaveSuccess();
    } catch (e) {
      console.error("Ошибка массового обновления:", e);
      alert(
        "Ошибка массового обновления: " +
          (e.response?.data?.error || e.message),
      );
    } finally {
      setApplyingToAll(false);
    }
  };

  const handleEditClick = async () => {
    const recordId = defectProperties?.Id || apiDataForPropertiesPanel?.ID;
    if (!recordId) {
      alert("Нет данных CombinedTable для редактирования.");
      return;
    }
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
      const response = await axios.get(
        `${baseUrl}/api/neural_network_module/combinedtable/?id=${recordId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data) {
        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        setEditModalData(data);
        setIsEditModalOpen(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setEditModalData(null);
    alert("Запись обновлена!");
    if (localFirstLevelClass && localSecondLevelClass) {
      fetchDefectCharacteristics(localFirstLevelClass, localSecondLevelClass);
    }
  };

  const renderEditableList = (field, label, dcItems = []) => {
    const selectedItems = formData[field]
      ? formData[field]
          .split(";")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
    const cleanerDcItems = dcItems.map((item) => item.trim());
    const allItems = [...new Set([...cleanerDcItems, ...selectedItems])];

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            margin: "8px 0",
            fontSize: "14px",
            fontWeight: "500",
            color: "#333",
          }}
        >
          {label}
        </div>
        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            maxHeight: "150px",
            overflowY: "auto",
          }}
        >
          {allItems.length === 0 ? (
            <div
              style={{
                padding: "8px",
                fontSize: "13px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Нет данных
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {allItems.map((item, idx) => {
                  const isChecked = selectedItems.includes(item);
                  return (
                    <tr
                      key={`${field}-${idx}`}
                      style={{
                        borderBottom:
                          idx < allItems.length - 1
                            ? "1px solid #dee2e6"
                            : "none",
                        backgroundColor:
                          idx % 2 === 0 ? "rgba(0, 0, 0, 0.02)" : "transparent",
                      }}
                    >
                      <td
                        style={{
                          padding: "6px 8px",
                          width: "20px",
                          verticalAlign: "middle",
                        }}
                      >
                        <CFormCheck
                          id={`${field}-${idx}`}
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) =>
                            handleCheckboxChange(field, item, e.target.checked)
                          }
                          style={{ margin: 0 }}
                        />
                      </td>
                      <td style={{ padding: "6px 8px", fontSize: "13px" }}>
                        <label
                          htmlFor={`${field}-${idx}`}
                          style={{
                            cursor: "pointer",
                            margin: 0,
                            display: "block",
                          }}
                        >
                          {item}
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const {
    defectCharacters = [],
    recommendations = [],
    causes = [],
  } = dcData ? extractDcFields(dcData) : {};

  const handleFirstLevelChange = async (e) => {
    const selectedValue = e.target.value;
    const currentSecondLevelClass = localSecondLevelClass;

    if (!selectedValue) {
      setLocalFirstLevelClass("");
      setEdited(true);
      setAllSecondLevelClasses([]);
      setLocalSecondLevelClass("");
      if (onFirstLevelChange) onFirstLevelChange("");
      if (onSecondLevelChange) onSecondLevelChange("");
      setSelectedDefectDisplay("");
      return;
    }

    const selectedFirstLevelClassObj = allFirstLevelClasses.find(
      (item) => item.ClassID === selectedValue,
    );

    if (
      currentSecondLevelClass &&
      objectData?.NTypeObj &&
      selectedFirstLevelClassObj
    ) {
      const firstLevelClassText = `${selectedFirstLevelClassObj.ClassID} - ${selectedFirstLevelClassObj.ClassName}`;
      setLoadingSecondLevelClasses(true);

      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
        const response = await axios.get(
          `${baseUrl}/api/neural_network_module/secondlevelclasses/`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              building_type_id: objectData.NTypeObj,
              first_level_class: firstLevelClassText,
            },
          },
        );

        const validDefects = response.data || [];
        const isCurrentDefectValid = validDefects.some(
          (d) => d.ClassID === currentSecondLevelClass,
        );

        if (isCurrentDefectValid) {
          setAllSecondLevelClasses(validDefects);
          setLocalFirstLevelClass(selectedValue);
          setEdited(true);
          fetchDefectCharacteristics(selectedValue, currentSecondLevelClass);
          if (onFirstLevelChange) onFirstLevelChange(selectedValue);
        } else {
          setPendingConstructionChange({
            selectedValue,
            validDefects,
          });
          setConfirmModalVisible(true);
        }
      } catch (error) {
        console.error("Ошибка при проверке допустимости дефекта:", error);
        setLocalFirstLevelClass(selectedValue);
        setEdited(true);
        setLocalSecondLevelClass("");
        if (onFirstLevelChange) onFirstLevelChange(selectedValue);
        if (onSecondLevelChange) onSecondLevelChange("");
        setSelectedDefectDisplay("");
      } finally {
        setLoadingSecondLevelClasses(false);
      }
    } else {
      setLocalFirstLevelClass(selectedValue);
      setEdited(true);
      if (onFirstLevelChange) onFirstLevelChange(selectedValue);

      if (selectedValue && objectData?.NTypeObj && selectedFirstLevelClassObj) {
        const firstLevelClassText = `${selectedFirstLevelClassObj.ClassID} - ${selectedFirstLevelClassObj.ClassName}`;
        fetchFilteredSecondLevelClasses(
          objectData.NTypeObj,
          firstLevelClassText,
        );
      } else if (!selectedValue) {
        setAllSecondLevelClasses([]);
      }

      setLocalSecondLevelClass("");
      if (onSecondLevelChange) onSecondLevelChange("");
      setSelectedDefectDisplay("");
    }
  };

  const handleConfirmOk = () => {
    setConfirmModalVisible(false);
    if (pendingConstructionChange) {
      setLocalFirstLevelClass(pendingConstructionChange.selectedValue);
      setEdited(true);
    }
    setPendingConstructionChange(null);
    if (onRequestOpenSettings) {
      onRequestOpenSettings();
    }
  };

  const handleConfirmCancel = () => {
    setConfirmModalVisible(false);
    if (pendingConstructionChange) {
      const { selectedValue, validDefects } = pendingConstructionChange;
      setLocalFirstLevelClass(selectedValue);
      setAllSecondLevelClasses(validDefects);

      setLocalSecondLevelClass("");
      if (onFirstLevelChange) onFirstLevelChange(selectedValue);
      if (onSecondLevelChange) onSecondLevelChange("");
      setSelectedDefectDisplay("");

      setDefectProperties(null);
      setDcData(null);
      setFormData((prev) => ({
        ...prev,
        DefectClassID: "",
        ClassName: "",
        Location: "",
        QuantitativeValue: prev.QuantitativeValue || "",
        Category: "",
        DefectCharacter: "",
        Recommendation: "",
        Cause: "",
      }));
      setEdited(true);
    }
    setPendingConstructionChange(null);
  };

  const handleSecondLevelChange = (e) => {
    const selectedValue = e.target.value;
    setLocalSecondLevelClass(selectedValue);

    const selectedDefectObj = allSecondLevelClasses.find(
      (item) => item.ClassID === selectedValue,
    );

    if (selectedDefectObj) {
      const defectDisplay = formatOption(selectedDefectObj);
      setSelectedDefectDisplay(defectDisplay);
    } else {
      setSelectedDefectDisplay("");
    }

    if (onSecondLevelChange) {
      onSecondLevelChange(selectedValue);
    }

    if (localFirstLevelClass && selectedValue) {
      fetchDefectCharacteristics(localFirstLevelClass, selectedValue);
    }
    setEdited(true);
  };

  return (
    <div
      style={{
        flex: 1,
        backgroundColor: "#fff",
        padding: "15px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        <span style={{ fontSize: "14px", fontWeight: "500" }}>
          Свойства дефекта
        </span>
      </div>

      <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
        <div style={{ flex: 1 }}>
          <label
            style={{ display: "block", marginBottom: "3px", fontSize: "12px" }}
          >
            Метка-конструкции:
            <CTooltip
              content={
                objectData?.NTypeObj
                  ? `Отображаются конструкции для типа здания ${objectData.NTypeObj}`
                  : "Выберите конструкцию, к которой относится дефект"
              }
              placement="top"
            >
              <span
                style={{
                  fontSize: "10px",
                  marginLeft: "5px",
                  cursor: "help",
                  padding: "0 4px",
                  border: "1px solid #000000",
                  borderRadius: "100%",
                }}
              >
                ?
              </span>
            </CTooltip>
          </label>
          <CFormSelect
            size="sm"
            value={localFirstLevelClass || ""}
            onChange={handleFirstLevelChange}
            style={{ fontSize: "12px", padding: "4px 8px" }}
          >
            <option value="">Выберите конструкцию</option>
            {allFirstLevelClasses.map((item) => (
              <option key={item.ID} value={item.ClassID}>
                {formatOption(item)}
              </option>
            ))}
          </CFormSelect>
        </div>

        <div style={{ flex: 1 }}>
          <label
            style={{ display: "block", marginBottom: "3px", fontSize: "12px" }}
          >
            Метка-дефект:
            <CTooltip
              content={
                localFirstLevelClass && objectData?.NTypeObj
                  ? `Отображаются дефекты для выбранной конструкции и типа здания ${objectData.NTypeObj}`
                  : "Выберите конкретный дефект для выбранной конструкции"
              }
              placement="top"
            >
              <span
                style={{
                  fontSize: "10px",
                  marginLeft: "5px",
                  cursor: "help",
                  padding: "0 4px",
                  border: "1px solid #000000",
                  borderRadius: "100%",
                }}
              >
                ?
              </span>
            </CTooltip>
          </label>
          <div style={{ position: "relative" }}>
            <CFormSelect
              size="sm"
              value={localSecondLevelClass || ""}
              onChange={handleSecondLevelChange}
              disabled={!localFirstLevelClass || loadingSecondLevelClasses}
              style={{ fontSize: "12px", padding: "4px 8px" }}
            >
              <option value="">Выберите дефект</option>
              {allSecondLevelClasses.map((item) => (
                <option key={item.ID} value={item.ClassID}>
                  {formatOption(item)}
                </option>
              ))}
            </CFormSelect>
            {loadingSecondLevelClasses && (
              <div
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              >
                <CSpinner size="sm" />
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CSpinner size="sm" />
        </div>
      ) : !localSecondLevelClass ? (
        <div
          style={{
            fontSize: "13px",
            color: "#999",
            textAlign: "center",
            marginTop: "20px",
          }}
        >
          Выберите дефект для просмотра свойств
        </div>
      ) : (
        <>
          <div>
            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
              <div style={{ flex: 1 }}>
                <CFormInput
                  label={
                    <span style={{ fontSize: "12px" }}>Наименование метки</span>
                  }
                  value={formData.ClassName}
                  onChange={(e) => handleChange("ClassName", e.target.value)}
                  size="sm"
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <CFormInput
                  label={
                    <span style={{ fontSize: "12px" }}>
                      Местоположение дефекта
                    </span>
                  }
                  value={formData.Location}
                  onChange={(e) => handleChange("Location", e.target.value)}
                  size="sm"
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                />
              </div>
            </div>
            <div style={{ flex: 1, marginBottom: "10px" }}>
              <CFormInput
                label={
                  <span style={{ fontSize: "12px" }}>
                    Количественный показатель
                  </span>
                }
                value={formData.QuantitativeValue}
                onChange={(e) =>
                  handleChange("QuantitativeValue", e.target.value)
                }
                size="sm"
                style={{ fontSize: "12px", padding: "4px 8px" }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "15px",
                alignItems: "flex-end",
              }}
            >
              <div style={{ flex: 1 }}>
                <CFormSelect
                  label={
                    <span style={{ fontSize: "12px" }}>Категория дефекта</span>
                  }
                  value={formData.Category}
                  onChange={(e) => handleChange("Category", e.target.value)}
                  size="sm"
                  style={{ fontSize: "12px", padding: "4px 8px" }}
                >
                  <option value="">-</option>
                  <option value="А">А</option>
                  <option value="Б">Б</option>
                  <option value="В">В</option>
                </CFormSelect>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  height: "32px",
                }}
              >
                <CFormCheck
                  label={
                    <span style={{ fontSize: "12px" }}>
                      Назначить фото главным
                    </span>
                  }
                  checked={!!formData.IsUnionToPhoto}
                  onChange={(e) =>
                    handleChange("IsUnionToPhoto", e.target.checked)
                  }
                  style={{ marginBottom: 0 }}
                />
              </div>
            </div>
            {renderEditableList(
              "DefectCharacter",
              "Характер дефекта",
              defectCharacters,
            )}
            {renderEditableList(
              "Recommendation",
              "Рекомендуемый метод устранения",
              recommendations,
            )}
            {renderEditableList("Cause", "Возможные причины", causes)}
          </div>
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginTop: "20px",
              flexWrap: "wrap",
            }}
          >
            <CTooltip
              content="Редактировать свойства в расширенном окне"
              placement="top"
            >
              <CButton
                color="secondary"
                size="sm"
                onClick={handleEditClick}
                style={{ fontSize: "12px", padding: "4px 8px" }}
              >
                Редактировать
              </CButton>
            </CTooltip>
            <CTooltip
              content="Применить текущие свойства ко всем дефектам этого типа"
              placement="top"
            >
              <CButton
                color="secondary"
                size="sm"
                onClick={handleApplyToAll}
                disabled={applyingToAll || !localFirstLevelClass}
                style={{ fontSize: "12px", padding: "4px 8px" }}
              >
                {applyingToAll ? "Применение..." : "Применить ко всем"}
              </CButton>
            </CTooltip>
            <CButton
              color="primary"
              size="sm"
              onClick={handleSave}
              disabled={!edited || saving}
              style={{ fontSize: "12px", padding: "4px 8px" }}
            >
              {saving ? "Сохранение..." : "Сохранить"}
            </CButton>
          </div>
        </>
      )}

      {/* Модальное окно подтверждения смены конструкции */}
      <CModal
        visible={confirmModalVisible}
        onClose={handleConfirmCancel}
        style={modalStyle}
      >
        <CModalHeader>
          <CModalTitle>Смена конструкции</CModalTitle>
        </CModalHeader>
        <CModalBody>
          Для текущей конструкции выбранный дефект не назначен. Добавить его в
          справочник?
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleConfirmCancel}>
            Нет
          </CButton>
          <CButton color="primary" onClick={handleConfirmOk}>
            Да
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Окно расширенного редактирования */}
      {isEditModalOpen && editModalData && (
        <AddCombined
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditModalData(null);
          }}
          objectId={objectData?.Id}
          isEdit={true}
          editData={editModalData}
          onEditSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default DefectPropertiesPanel;
